import React from 'react';
import { Lightbulb, Loader2 } from 'lucide-react';

interface AIRecommendationsCardProps {
  recommendations: string[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const AIRecommendationsCard: React.FC<AIRecommendationsCardProps> = ({
  recommendations,
  isLoading,
  error,
  onRefresh
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 relative">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
          <Lightbulb className="mr-2 text-purple-600" size={24} />
          Nos recommandations
        </h2>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-500">Génération des recommandations en cours...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-100 rounded-md p-4 mb-4">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={onRefresh}
            className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Réessayer
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {recommendations.length > 0 ? (
            <ul className="space-y-4">
              {recommendations.map((recommendation, index) => (
                <li key={index} className="flex">
                  <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-medium mr-3">
                    {index + 1}
                  </span>
                  <div className="flex-1 leading-relaxed text-gray-700">
                    {(() => {
                      // Séparer automatiquement au premier point
                      const firstSentenceEnd = recommendation.indexOf('.');
                      if (firstSentenceEnd !== -1 && firstSentenceEnd < recommendation.length - 1) {
                        const firstSentence = recommendation.substring(0, firstSentenceEnd + 1).trim();
                        const solution = recommendation.substring(firstSentenceEnd + 1).trim();
                        
                        return (
                          <>
                            <div>{firstSentence}</div>
                            {solution && (
                              <div className="mt-2 pl-4 border-l-2 border-blue-200 bg-blue-50 p-2 rounded-r">
                                <span className="font-medium text-blue-700">Solution:</span>
                                <span className="text-gray-700 ml-1">{solution}</span>
                              </div>
                            )}
                          </>
                        );
                      }
                      
                      // Si pas de point trouvé, afficher tel quel
                      return <div>{recommendation}</div>;
                    })()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 italic">Aucune recommandation disponible.</p>
          )}
          
        </div>
      )}
      
    </div>
  );
};

export default AIRecommendationsCard;
